"use client";

import { useSelection, useToken } from "@frend-digital/centra/client";
import type { PaymentResult } from "@frend-digital/centra-types/orders";
import { events } from "@frend-digital/events";
import { gtag, sendGTMEvent } from "@frend-digital/events/gtm";
import { useEffect, useRef } from "react";
import { useBeforeGtmEvent } from "@/features/global/Events";
import { setUserData } from "@/features/global/user_data";
import type { ProductCard, ProductDetails } from "@/lib/centra/formatters";
import {
  productCardsToGTAGProducts,
  productDetailsToGTAGProduct,
  selectionItemToGTAGProduct,
} from "./utils";

export const ViewItemEvent = ({
  item,
  price,
}: {
  item: ProductDetails;
  price: number;
}) => {
  const hasRan = useRef(false);
  const beforeGtmEvent = useBeforeGtmEvent();
  const { data: currency } = useSelection({
    select: data => data.selection?.currency,
  });
  const gtmProduct = productDetailsToGTAGProduct(item, currency || "USD");

  useEffect(() => {
    if (!currency || !beforeGtmEvent || hasRan.current) return;

    gtag.viewItem(
      {
        currency,
        value: price,
        items: [gtmProduct],
      },
      beforeGtmEvent,
    );

    hasRan.current = true;
  }, [item, price, currency, beforeGtmEvent, gtmProduct]);

  return null;
};

export const ViewItemListEvent = ({
  id,
  name,
  items,
}: {
  id: string;
  name: string;
  items: ProductCard[];
}) => {
  const hasRan = useRef(false);
  const beforeGtmEvent = useBeforeGtmEvent();
  const { data: currency } = useSelection({
    select: data => data.selection?.currency,
  });

  const products = productCardsToGTAGProducts({
    categoryPage: name,
    currency: currency || "",
    products: items,
  });

  useEffect(() => {
    if (!currency || !beforeGtmEvent || hasRan.current) return;

    gtag.viewItemList(
      {
        item_list_name: name,
        item_list_id: id,
        currency: currency,
        items: products,
      },
      beforeGtmEvent,
    );
    hasRan.current = true;
  }, [currency, items, id, name, beforeGtmEvent, products]);

  return null;
};

export const BeginCheckoutEvent = () => {
  const token = useToken();

  const { data: selection } = useSelection();
  const hasRan = useRef(false);

  useEffect(() => {
    if (selection) {
      if (hasRan.current) return;
      events.dispatch("begin_checkout", selection, {
        dispatcher: "centra",
      });
      hasRan.current = true;
    }
  }, [token, selection]);

  return null;
};

export const AddPaymentInfoEvent = () => {
  useEffect(() => {
    events.dispatch(
      "add_payment_info",
      {
        paymentMethod: "adyen",
      },
      {
        dispatcher: "centra",
      },
    );
  }, []);

  return null;
};

export const NewlsetterSubscribeEvent = () => {
  useEffect(() => {
    const controller = new AbortController();

    const handleKlaviyoSubmit = async (e: Event) => {
      const event = e as Partial<{
        detail: {
          type: string;
          metaData: {
            $email: string;
            email: string;
          };
        };
      }>;

      if (event.detail?.type !== "submit") return;

      const email =
        event.detail?.metaData?.$email || event.detail?.metaData?.email || null;

      if (!email) return;

      const userdata = await setUserData({
        user_data: {
          email: email,
        },
      });

      sendGTMEvent({
        event: "newsletter",
        ecommerce: null,
        user_data: userdata,
      });
    };

    window.addEventListener("klaviyoForms", handleKlaviyoSubmit, {
      signal: controller.signal,
    });

    return () => {
      controller.abort();
    };
  }, []);

  return null;
};

export const PurchaseEvent = ({ order }: { order: PaymentResult["order"] }) => {
  const beforeGtmEvent = useBeforeGtmEvent();
  const hasRan = useRef(false);

  useEffect(() => {
    if (!order || hasRan.current) return;

    gtag.purchase(
      {
        coupon: order.discounts?.vouchers?.map(v => v.voucher)?.join(";"),
        currency: order.currency!,
        items: (order.items || []).map(item =>
          selectionItemToGTAGProduct(item, order?.currency!),
        ),
        transaction_id: order.order as string,
        tax: order.totals.grandTotalPriceTaxAsNumber || 0,
        value: order.totals.grandTotalPriceAsNumber || 0,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        shipping_tier: order.shippingMethodName as string,
        shipping: order.totals.shippingPriceAsNumber || 0,
        payment_type: order.paymentMethodName,
        value_ink_vat: order.totals.grandTotalPriceAsNumber || 0,
        value_ex_vat:
          (order.totals.grandTotalPriceAsNumber || 0) -
          (order.totals.grandTotalPriceTaxAsNumber || 0),
      },
      beforeGtmEvent,
    );

    hasRan.current = true;
  }, [beforeGtmEvent, order]);

  return null;
};
